import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAction } from "wasp/client/operations";
import { createEvent } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  Save,
  Music,
  GraduationCap,
  Briefcase,
  Coffee,
  Globe,
  Users,
  Image as ImageIcon,
  AlertCircle,
  Trash2,
} from "lucide-react";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { useLocationMembership } from "../../client/hooks/useLocationMembership";
import { AudienceSelect, type Audience } from "../../client/components/AudienceSelect";
import { MembersOnlyNotice } from "../../client/components/MembersOnlyNotice";

interface EventFormData {
  title: string;
  description: string;
  eventType: string;
  startTime: string;
  endTime: string;
  timezone: string;
  venueName: string;
  venueAddress: string;
  isVirtual: boolean;
  meetingUrl: string;
  coverImage: string;
  maxAttendees: string;
  requiresRegistration: boolean;
  visibility: Audience;
}

const EVENT_TYPES = [
  { value: "workshop", label: "Workshop", icon: GraduationCap },
  { value: "meeting", label: "Meeting", icon: Briefcase },
  { value: "social", label: "Social", icon: Coffee },
  { value: "cultural", label: "Cultural", icon: Music },
];

export default function AddEventPage() {
  const { isMember, isLoading: membershipLoading, locationCode } = useLocationMembership();
  useRequireSectionVisible("showEvent");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const createEventFn = useAction(createEvent);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<EventFormData>({
    title: "",
    description: "",
    eventType: "social",
    startTime: "",
    endTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    venueName: "",
    venueAddress: "",
    isVirtual: false,
    meetingUrl: "",
    coverImage: "",
    maxAttendees: "",
    requiresRegistration: false,
    visibility: "PUBLIC",
  });

  const handleChange = (field: keyof EventFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Event title is required";
    if (!formData.description.trim()) newErrors.description = "Event description is required";
    if (!formData.startTime) newErrors.startTime = "Start time is required";
    if (!formData.endTime) newErrors.endTime = "End time is required";
    if (formData.startTime && formData.endTime && new Date(formData.endTime) <= new Date(formData.startTime)) {
      newErrors.endTime = "End time must be after start time";
    }
    if (formData.isVirtual && !formData.meetingUrl.trim()) {
      newErrors.meetingUrl = "Meeting URL is required for virtual events";
    }
    if (!formData.isVirtual && !formData.venueName.trim()) {
      newErrors.venueName = "Venue name is required";
    }
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await createEventFn({
        locationId: selectedLocation!.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        eventType: formData.eventType,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        timezone: formData.timezone,
        venueName: formData.venueName.trim(),
        venueAddress: formData.venueAddress.trim(),
        isVirtual: formData.isVirtual,
        meetingUrl: formData.meetingUrl.trim(),
        coverImage: formData.coverImage.trim(),
        maxAttendees: parseInt(formData.maxAttendees) || undefined,
        requiresRegistration: formData.requiresRegistration,
        visibility: formData.visibility,
      });
      handleGoBack();
    } catch (error) {
      console.error("Error creating event:", error);
      setErrors({ submit: "Failed to create event." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/events`);
    } else {
      navigate("/events");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to create events.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!membershipLoading && !isMember) {
    return (
      <IndividualEditPageLayout title="Create Event" icon={Calendar}>
        <MembersOnlyNotice locationCode={locationCode} />
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton onClick={handleSubmit} disabled={isSubmitting} icon={Save}>
        {isSubmitting ? "Creating..." : "Create Event"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>Cancel</ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Event Settings" hideOnMobile>
        <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
          <input
            type="checkbox"
            checked={formData.isVirtual}
            onChange={(e) => handleChange("isVirtual", e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Video className="w-4 h-4" /> Virtual Event
          </span>
        </label>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Create Event"
      subtitle="Gather the community together"
      icon={Calendar}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="event-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div className="flex bg-slate-100 p-1 rounded-xl w-fit overflow-x-auto no-scrollbar">
            {EVENT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleChange("eventType", type.value)}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                  formData.eventType === type.value ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <type.icon className="w-4 h-4" /> {type.label}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Event Title</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g., Annual Tech Meetup 2024"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              placeholder="What makes this event special?"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Clock className="w-4 h-4 text-orange-600" /> Timing</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Start Time</label>
                <input
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) => handleChange("startTime", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-500/20 transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">End Time</label>
                <input
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) => handleChange("endTime", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-500/20 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><MapPin className="w-4 h-4 text-blue-600" /> Venue</h3>
            {formData.isVirtual ? (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Meeting URL</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="url"
                    value={formData.meetingUrl}
                    onChange={(e) => handleChange("meetingUrl", e.target.value)}
                    placeholder="https://zoom.us/j/..."
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Venue Name</label>
                  <input
                    type="text"
                    value={formData.venueName}
                    onChange={(e) => handleChange("venueName", e.target.value)}
                    placeholder="e.g., Grand Ballroom"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Address</label>
                  <input
                    type="text"
                    value={formData.venueAddress}
                    onChange={(e) => handleChange("venueAddress", e.target.value)}
                    placeholder="123 Street Name..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Users className="w-4 h-4 text-purple-600" /> Additional Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Max Attendees (Optional)</label>
              <input
                type="number"
                value={formData.maxAttendees}
                onChange={(e) => handleChange("maxAttendees", e.target.value)}
                placeholder="Unlimited"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500/20 transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Cover Image URL</label>
              <div className="relative">
                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.coverImage}
                  onChange={(e) => handleChange("coverImage", e.target.value)}
                  placeholder="https://..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500/20 transition-all"
                />
              </div>
            </div>
          </div>
        </div>

        <AudienceSelect
          value={formData.visibility}
          onChange={(value) => handleChange("visibility", value)}
        />
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
